import { useCallback, useEffect, useState } from "react";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { OrgSessionEvents } from "src/platform/constants/Events";
import { StorageKeys } from "src/platform/constants/Storage";
import { eventBus } from "src/platform/events/EventBus";

export interface ActiveOrganizationState {
  orgAlias: string | null;
  setActiveOrg: (alias: string) => void;
  clearActiveOrg: () => void;
}

/**
 * @deprecated Use `useOrgSession` from `@domains/auth/hooks/useOrgSession` instead.
 *   This wrapper is kept for backward compatibility with existing consumers.
 *
 * Now delegates to OrgSessionManager (single source of truth) and subscribes
 * to ORG_SESSION_CHANGED events for mid-session org switches.
 */
export function useActiveOrganization(): ActiveOrganizationState {
  const [orgAlias, setOrgAlias] = useState<string | null>(
    orgSessionManager.getOrgAlias(),
  );

  // Subscribe to org session changes so the hook updates on mid-session switches
  useEffect(() => {
    eventBus.subscribeGlobal(
      OrgSessionEvents.ORG_SESSION_CHANGED,
      (payload: { orgAlias: string }) => {
        setOrgAlias(payload.orgAlias);
      },
    );
  }, []);

  /**
   * Called by OrganizationDetails after navigation to sync React state
   * with the OrgSessionManager-derived alias. Does NOT write to localStorage —
   * the JWT already contains the authoritative value.
   */
  const setActiveOrg = useCallback((alias: string) => {
    setOrgAlias(alias);
  }, []);

  const clearActiveOrg = useCallback(() => {
    // JWT is cleared by clearAllRushaStorage() on logout.
    // Also clear any org display metadata keys.
    localStorage.removeItem(StorageKeys.SELECTED_ORGANIZATION);
    localStorage.removeItem(StorageKeys.CURRENT_ORG_ALIAS);
    setOrgAlias(null);
  }, []);

  return { orgAlias, setActiveOrg, clearActiveOrg };
}
